import React from "react";
import { API_ENDPOINTS } from "config/apiConfig";
import {
  Delete,
  CompanyFilter,
  CompanyListPaginate,
  Company,
  CompanyDetail,
} from "common/interfaces";
import { PAGINATION } from "common/enums/Pagination.enum";
import { baseApi } from "./baseApi";

export const companyApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get Company
    getCompanies: builder.query<
      CompanyListPaginate,
      { filter?: CompanyFilter; page?: number }
    >({
      query: ({ filter, page }) => ({
        url: API_ENDPOINTS.COMPANY_INFO,
        params: {
          page: page || PAGINATION.PAGE,
          limit: PAGINATION.LIMIT,
          ...filter,
        },
      }),
      providesTags: ["Company"],
    }),

    // Get Company Detail
    getCompanyDetail: builder.query<CompanyDetail, { code: string }>({
      query: ({ code }) => ({
        url: `${API_ENDPOINTS.COMPANY_INFO}/${code}?transaction_overview=1`,
      }),
      providesTags: ["Company"],
    }),

    // Update Company
    updateCompany: builder.mutation<
      Company,
      { companyCode: string; status: number }
    >({
      query: ({ companyCode, status }) => ({
        url: `${API_ENDPOINTS.COMPANY_INFO_DELETE}/${companyCode}`,
        method: "PUT",
        body: { status },
      }),
      invalidatesTags: ["Company"],
    }),

    // Delete Company
    deleteCompany: builder.mutation<Delete, React.Key[]>({
      query: (codes) => ({
        url: API_ENDPOINTS.COMPANY_INFO_DELETE,
        method: "DELETE",
        body: { codes },
      }),
      invalidatesTags: ["Company"],
    }),

    // Download CSV
    downloadCompanyCsv: builder.mutation<
      Blob,
      { filter: CompanyFilter; fileName: string }
    >({
      query: ({ filter, fileName }) => ({
        url: API_ENDPOINTS.COMPANY_INFO_CSV,
        method: "GET",
        params: filter,
        responseHandler: async (response) => {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", fileName);
          document.body.appendChild(link);
          link.click();
          link.remove();
          return blob;
        },
      }),
    }),
  }),
});

export const {
  useGetCompaniesQuery,
  useGetCompanyDetailQuery,
  useUpdateCompanyMutation,
  useDeleteCompanyMutation,
  useDownloadCompanyCsvMutation,
} = companyApi;
